<template>
  <div class="flex-1">
    <div class="d-flex">
      <div
        v-if="showConditionOptions"
        class="flex-1 mb-4 mx-1"
      >
        <fieldset>
          <FormLabel
            id="data-type-select"
            class="flex-0"
            label="Data type"
          />
          <Multiselect
            id="data-type-select"
            :disabled="disabled"
            :options="conditionOptions"
            :searchable="true"
            :placeholder="readOnly ? '—' : 'Select data type'"
            :value="selection.condition === 'requisiteSets'?'requirementSets': selection.condition "
            :custom-label="decamelize"
            @input="updateSelectionField('condition', $event || selection.condition)"
          />
        </fieldset>
      </div>
    </div>
    <div>
      <div
        v-if="selection.condition !== 'none'"
        class="flex-1 mb-2"
      >
        <div
          v-if="selection.condition === 'numberOf'"
          class="d-flex mb-2 mx-1 align-items-center"
        >
          <div class="px-2">At least</div>
          <div v-if="readOnly">
            {{ selection.number }}
          </div>
          <input
            v-else
            :disabled="disabled"
            class="form-control"
            style="width:auto"
            type="number"
            min="0"
            :value="selection.number"
            @input="updateSelectionField('number', $event.target.value)"
          />
          <div class="px-2">of the following:</div>
        </div>
        <div
          v-if="selection.condition === 'freeformText'"
          class="flex-grow d-flex mb-2 mx-1 align-items-center"
        >
          <div v-if="readOnly">
            {{ selection.text }}
          </div>
          <input
            v-else
            :disabled="disabled"
            class="form-control"
            type="text"
            :value="selection.text"
            @input="updateSelectionField('text', $event.target.value)"
          />
        </div>
        <div
          v-if="!shouldShowSubSelections && selection.condition !== 'freeformText'"
          class="flex-1 mb-2 mx-1"
          data-test="requisite-data-select-input"
        >
          <FormGroupedCourseSelect
            v-if="selection.condition === 'courses'"
            :excludeIds="[formValue.id]"
            :value="selection.values"
            :config="{ includePending: false }"
            :useFetchingRequisitesLatestRevisionSettings="useFetchingRequisitesLatestRevisionSettings"
            @input="updateSelectionField('values', $event)"
          />
          <FormGroupedCourseSetSelect
            v-else-if="selection.condition === 'courseSets'"
            :value="selection.values"
            @input="updateSelectionField('values', $event)"
          />
          <FormGroupedRequirementSetSelect
            v-else-if="selection.condition === 'requisiteSets'"
            :value="selection.values"
            @input="updateSelectionField('values', $event)"
          />
          <FormProgramSelect
            v-else-if="selection.condition === 'programs'"
            :value="selection.values"
            :placeholder="readOnly ? '...' : 'Type to search'"
            :config="{
              multiple: true,
              includePending: true,
              useFetchingRequisitesLatestRevisionSettings: useFetchingRequisitesLatestRevisionSettings,
            }"
            :disabled="disabled"
            @input="updateSelectionField('values', $event)"
          />
        </div>
      </div>
    </div>

    <div v-if="shouldShowSubSelections">
      <div
        v-for="(subSelection, selectionIndex) in selection.subSelections"
        :key="subSelection.id"
        class="d-flex"
      >
        <div class="flex-1 mb-2 mx-1">
          <RequisiteDataSelect
            :selection-data="subSelection"
            :depth="depth + 1"
            :disabled="disabled"
            :read-only="readOnly"
            @updatedSelection="updateSubSelection($event, selectionIndex)"
          />
        </div>
        <div>
          <button
            class="btn btn-sm btn-danger"
            type="button"
            @click="deleteSubSelection(selectionIndex)"
          >
            <i class="material-icons">remove_circle</i>
          </button>
        </div>
      </div>
      <button
        class="btn btn-sm btn-primary"
        type="button"
        @click="addSubSelection"
      >
        <i class="material-icons">add_circle</i>
      </button>
    </div>
  </div>
</template>

<script>
import Multiselect from '@/components/Multiselect'
import { mapGetters } from 'vuex'
import { inject, computed } from '@vue/composition-api'
import { generateToken } from '@shared/utils/common'
import useSchoolSettings from '@/components/composables/useSchoolSettings'
import FormGroupedCourseSelect from '@/components/Forms/FormGroupedCourseSelect'
import FormGroupedCourseSetSelect from '@/components/Forms/FormGroupedCourseSetSelect'
import FormGroupedRequirementSetSelect from '@/components/Forms/FormGroupedRequirementSetSelect'
import FormLabel from '@/components/Forms/FormLabel'
import FormProgramSelect from '@cm/client/components/Forms/FormProgramSelect'

const DEFAULT_CONDITION_OPTIONS = [
  'courses',
  'programs',
  'courseSets',
]

export default {
  name: 'RequisiteDataSelect',
  components: {
    Multiselect,
    FormGroupedCourseSelect,
    FormGroupedCourseSetSelect,
    FormGroupedRequirementSetSelect,
    FormLabel,
    FormProgramSelect,
  },
  props: {
    depth: { type: Number, default: 0 },
    selectionData: { type: Object },
    type: { type: String },
    disabled: { type: Boolean, default: false },
    readOnly: { type: Boolean, default: false },
    isProgram: { type: Boolean, default: false },
    showConditionOptions: { type: Boolean, default: true },
  },
  data () {
    return {
      conditionOptions: [],
      selection: this.selectionData || this.defaultSelection(),
    }
  },
  setup () {
    const formValue = inject('formValue', {}) || {}
    const { schoolSettings, getSettings, settingsLoaded } = useSchoolSettings()

    if (!settingsLoaded.value) getSettings()

    const useFetchingRequisitesLatestRevisionSettings =
      computed(() => schoolSettings.value?.enableFetchingRequisitesWithLatestRevisions)

    return {
      formValue,
      useFetchingRequisitesLatestRevisionSettings,
    }
  },
  computed: {
    ...mapGetters([ 'school' ]),
    shouldShowSubSelections () {
      return this.selection.condition === 'anyOf' || this.selection.condition === 'allOf' || this.selection.condition === 'numberOf'
    },
  },
  watch: {
    type: {
      immediate: true,
      handler (val) {
        // certain types allow for a "none" condition
        // This watcher ensures that option only shows up when the "allowed" types are selected
        if (val === 'averageGrade' || val === 'minimumGrade' || val === 'minimumCredits' || val === 'minimumResidencyCredits') {
          this.conditionOptions = [
            ...DEFAULT_CONDITION_OPTIONS,
            'none',
          ]
        } else {
          this.conditionOptions = [
            ...DEFAULT_CONDITION_OPTIONS,
          ]
          if (this.isProgram) { this.conditionOptions.push('requirementSets') }
        }
      },
    },
  },
  created () {
    if (!this.selection.id) this.selection.id = generateToken(8)
  },
  methods: {
    deleteSubSelection (selectionIndex) {
      this.$delete(this.selection.subSelections, selectionIndex)
      this.$emit('updatedSelection', this.selection)
    },
    addSubSelection () {
      this.selection.subSelections.push(this.defaultSelection())
      this.$emit('updatedSelection', this.selection)
    },
    updateSubSelection (subSelection, selectionIndex) {
      if (typeof selectionIndex === 'undefined') {
        this.selection.subSelection = subSelection
      } else {
        this.selection.subSelections[selectionIndex] = subSelection
      }
      this.$emit('updatedSelection', this.selection)
    },
    updateSelectionField (key, value) {
      this.selection[key] = value

      if (key === 'condition' && value === 'requirementSets') { this.selection['condition'] = 'requisiteSets' }

      if (key === 'condition') this.updateSelectionCondition(value)
      if (key === 'number') this.$set(this.selection, key, value)

      this.$emit('updatedSelection', this.selection)
    },
    updateSelectionCondition (condition) {
      if (this.shouldShowSubSelections) {
        if (!this.selection.subSelections) {
          this.selection.subSelections = []
        }
      } else {
        this.selection.subSelections = undefined
      }

      if (condition === 'numberOf') {
        this.selection.number = 1
      } else {
        this.selection.number = undefined
      }
      this.selection.values = []
    },
    defaultSelection () {
      return {
        condition: 'courses',
        number: undefined,
        subSelections: undefined,
        values: [],
      }
    },
  },
}
</script>

<style scoped>
[class*="col"] {
  padding-bottom: 0.8rem;
}
</style>
